import ButtonBase from '@mui/material/ButtonBase';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';

import type { LinkProps } from '@tanstack/react-router';
import type { Ref } from 'react';
import type { TabProps } from './tab.js';

export type UnderlineTabProps = TabProps & {
	/** Renders the tab in an amber "hidden" treatment (e.g. tabs kept off by default for a project). */
	isNormallyHidden?: boolean;
	/** Native tooltip applied to the tab's clickable element. */
	title?: string;
	/** Extra classes applied to the label text, e.g. to tint it while an action is pending. */
	labelClassName?: string;
	/** Ref to the tab's root element, e.g. to anchor a popover to the tab. */
	ref?: Ref<HTMLDivElement>;
};

/**
 * Tab renderer with a persistent bottom border that fills in when selected, so switching tabs
 * doesn't shift the row's layout. Pass to a `Tabs` component's `renderTab` prop.
 *
 * @param props - Tab props from the parent `Tabs` component, plus underline-specific options.
 * @param props.isNormallyHidden - Whether to use the amber "hidden" treatment.
 * @param props.title - Native tooltip for the clickable element.
 * @param props.labelClassName - Extra classes applied to the label text.
 * @param props.ref - Ref to the tab's root element, e.g. to anchor a popover to the tab.
 * @returns The rendered tab, as a router `Link` when `linkProps` is set and not disabled, otherwise a button.
 */
export function UnderlineTab({
	isSelected,
	label,
	labelAdornEnd,
	testId,
	linkProps,
	disabled,
	isNormallyHidden = false,
	title,
	labelClassName,
	onClick,
	onContextMenu,
	ref,
}: UnderlineTabProps) {
	const className = clsx(
		'relative z-10 flex items-end rounded-t-sm px-3 py-2 align-bottom transition-colors duration-50',
		disabled ? 'text-gray-a6'
		: isNormallyHidden ? 'text-amber-a11 hover:bg-amber-a3'
		: 'text-gray-a10 hover:bg-gray-a3',
		isSelected && (isNormallyHidden ? 'bg-amber-a3 text-amber-a12' : 'text-gray-a12'),
	);
	const children = (
		<div className='flex items-center gap-1.5'>
			<p className={labelClassName}>{label}</p>
			{labelAdornEnd !== null && labelAdornEnd !== undefined && labelAdornEnd !== false && (
				<div className='flex h-0 items-center gap-1.5'>{labelAdornEnd}</div>
			)}
		</div>
	);
	return (
		<div
			ref={ref}
			className={clsx(
				'relative grid w-max max-w-48 place-items-stretch border-b-3',
				isSelected ?
					isNormallyHidden ? 'border-amber-9'
					:	'border-blue-9'
				: isNormallyHidden ? 'border-amber-a6'
				: 'border-transparent',
			)}
			onContextMenu={onContextMenu}
		>
			{linkProps && !disabled ?
				<Link
					{...(linkProps as LinkProps)}
					className={className}
					data-block-save-on-click
					data-testid={testId}
					onClick={onClick}
					title={title}
				>
					{children}
				</Link>
			:	<ButtonBase
					type='button'
					className={className}
					disabled={disabled}
					data-testid={testId}
					onClick={disabled ? undefined : onClick}
					title={title}
				>
					{children}
				</ButtonBase>
			}
		</div>
	);
}
